import posthogJs from 'posthog-js';

/*
 * Frontend PostHog wrapper — product-analytics counterpart to sentry.js.
 *
 * Bootstrapped once at module-load via a side-effect import from
 * index.js. Empty REACT_APP_POSTHOG_KEY → init skipped entirely (no
 * console noise in dev, no events leaked from local laptops). NOTE the
 * SDK ships in the bundle either way — "no analytics" means unarmed,
 * not absent — which is why the policy interlock below exists.
 *
 * What this enables:
 *   * Autocapture for pageviews + DOM clicks (zero per-callsite work)
 *   * Explicit funnel events via the `track(...)` helper in
 *     utils/analytics.js — see that file for the per-event payload
 *     shape that auto-attaches { is_anon, anon_id, user_id }.
 *   * Anon → user identity merge via posthog.identify(userId, ...)
 *     called from RegisterPage / LoginPage / useAuthBootstrap.
 *
 * Config rationale:
 *   * persistence: 'localStorage' — no third-party cookies, lighter
 *     privacy story, no consent banner required for v1.
 *   * respect_dnt: true — users sending Do Not Track aren't tracked.
 *   * capture_pageview: true — covers SPA route changes via the
 *     PostHog history-listener, no manual hook in App.js needed.
 *   * autocapture: true — picks up the long tail of click events
 *     beyond the 13 explicit funnel events we ship in this cycle.
 *
 * The exported `posthog` object is a no-op stub when init was
 * skipped, so callers (analytics.js, identify sites) don't need
 * defensive null checks.
 */

// PRIVACY-POLICY INTERLOCK — do not remove.
//
// /privacy currently tells users: "If we add analytics tools in the
// future, we will update this Policy and, where the law requires it, ask
// for your consent before we begin." Setting REACT_APP_POSTHOG_KEY is
// exactly that moment, and it takes no code change — so without this
// guard, turning analytics on in a dashboard silently makes a published
// privacy promise false. (Verified 2026-07-30: no key is baked into the
// live bundle, so the policy is accurate TODAY.)
//
// Flipping this to true is the ACKNOWLEDGEMENT that
// frontend/src/pages/PrivacyPage.js's "Analytics and advertising"
// section has been updated to describe what PostHog actually collects.
// Until then a key is ignored and we say so loudly.
const PRIVACY_POLICY_COVERS_ANALYTICS = false;

const rawKey = process.env.REACT_APP_POSTHOG_KEY;
if (rawKey && !PRIVACY_POLICY_COVERS_ANALYTICS) {
  // eslint-disable-next-line no-console
  console.warn(
    '[posthog] A key is set but analytics is DISABLED: the privacy policy ' +
      'has not been updated to disclose it. Update PrivacyPage.js, then set ' +
      'PRIVACY_POLICY_COVERS_ANALYTICS = true in posthog.js.',
  );
}
const key = PRIVACY_POLICY_COVERS_ANALYTICS ? rawKey : undefined;
const apiHost = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const environment =
  process.env.REACT_APP_POSTHOG_ENVIRONMENT ||
  process.env.NODE_ENV ||
  'development';

let initialised = false;

if (key) {
  posthogJs.init(key, {
    api_host: apiHost,
    persistence: 'localStorage',
    autocapture: true,
    capture_pageview: true,
    respect_dnt: true,
    // Tag every event with the deploy environment as a super-property
    // so PostHog can split prod / staging / dev without separate keys.
    loaded: (ph) => {
      ph.register({ environment });
      // Expose for browser-console debugging in dev only. Avoid
      // window-pollution in prod where it would also surface in
      // attacker reconnaissance scans.
      if (environment !== 'production') {
        // eslint-disable-next-line no-undef
        window.__posthog = ph;
      }
    },
  });
  initialised = true;
  // eslint-disable-next-line no-console
  console.log(`[posthog] initialised env=${environment}`);
}

// No-op stub for code paths that run before init OR when key is
// empty. Same shape as the real SDK for the methods we call across
// the app — capture, identify, register, reset. Adding a method here
// later costs nothing if the codebase grows new callsites.
const noopStub = {
  capture: () => {},
  identify: () => {},
  register: () => {},
  reset: () => {},
  isFeatureEnabled: () => false,
  getDistinctId: () => undefined,
};

export const posthog = initialised ? posthogJs : noopStub;
export const isPosthogInitialised = () => initialised;
